import { useEffect, useMemo, useState } from 'react';
import { isString } from '@/utils/isString';

function getMediaQueryList(query: string) {
  if (process.env.NODE_ENV === 'test') {
    return {
      matches: false,
      media: query,
      onchange: null,
      addEventListener: () => {},
      removeEventListener: () => {},
      dispatchEvent: () => false,
      addListener: () => {},
      removeListener: () => {},
    } as MediaQueryList;
  }

  const mql = window.matchMedia(query);

  // polyfill old implementations that don't inherit from EventTarget
  if (typeof mql.addEventListener !== 'function') {
    mql.addEventListener = (eventName: 'change', callback: EventListener) =>
      mql.addListener(callback);
    mql.removeEventListener = (eventName: 'change', callback: EventListener) =>
      mql.removeListener(callback);
  }

  return mql;
}

export function useMediaQuery(query: string | MediaQueryList) {
  const mql = useMemo(() => {
    if (isString(query)) {
      return getMediaQueryList(query);
    }

    return query;
  }, [query]);
  const [matches, setMatches] = useState(mql.matches);

  useEffect(() => {
    const updateMatches = () => setMatches(mql.matches);
    mql.addEventListener('change', updateMatches);
    return () => mql.removeEventListener('change', updateMatches);
  }, [mql]);

  return matches;
}

const isSmallMql = getMediaQueryList('(max-width: 768px)');

export function useIsSmall() {
  return useMediaQuery(isSmallMql);
}

export const isSmall = () => isSmallMql.matches;
